import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { Loader2 } from 'lucide-react';
import { SEO } from "../components/SEO.tsx";

interface TermsContent {
  id: string;
  title: string;
  content: string;
  version: number;
  updated_at: string;
}

export function TermsAndConditions() {
  const [content, setContent] = useState<TermsContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .rpc('get_active_site_content', { p_content_type: 'terms' });

      if (error) throw error;

      if (data && data.length > 0) {
        setContent(data[0]);
      } else {
        await createInitialTerms();
        await fetchContent();
      }
    } catch (err) {
      setError('Failed to load terms and conditions');
      logger.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createInitialTerms = async () => {
    const initialTerms = {
      title: "Terms and Conditions",
      content: `1. Introduction

Welcome to Wayja, a prediction markets platform. These Terms and Conditions govern your use of our platform and services. By accessing or using Wayja, you agree to be bound by these terms.

2. Definitions

• "Platform" refers to the Wayja prediction markets website and services
• "User" refers to any individual or entity using the Platform
• "Market" refers to a prediction market created on the Platform
• "Trade" refers to a user's stake in a particular market outcome
• "Service Fee" refers to the 8% fee charged on all trade placements

3. Account Registration

To use our Platform, you must register for an account. You agree to provide accurate and complete information during registration and to keep your account credentials secure. You are responsible for all activities that occur under your account.

4. Platform Rules

• Users must be at least 18 years old to use the Platform
• Users may not create multiple accounts
• Users must not engage in market manipulation or fraudulent activities
• Users must not use automated trading bots without explicit permission
• Users must comply with all applicable laws and regulations

5. Market Creation and Trading

Users may create markets subject to approval by our administrators. All markets must have clear, objective resolution criteria. Trades may be placed at any time before market resolution. Market creators must provide sufficient information for participants to make informed decisions.

6. Service Fees

The Platform charges a service fee of 8% on all trade placements. This fee is automatically deducted when placing a trade. For example, if you place a $100 trade, the service fee would be $8. The service fee is non-refundable and applies to all trade placements regardless of the outcome.

7. Market Resolution

Markets are resolved by administrators based on objective criteria. Resolution decisions are final and binding. Users may appeal resolution decisions within 24 hours of the resolution date.

8. User Conduct

Users must maintain respectful and professional conduct on the Platform. Prohibited activities include:
• Harassment or abuse of other users
• Posting of inappropriate or offensive content
• Attempting to manipulate market outcomes
• Sharing of account credentials
• Using the Platform for illegal purposes

9. Intellectual Property

All content and materials on the Platform are owned by Wayja or its licensors. Users retain rights to their market content but grant Wayja a license to use, modify, and display such content on the Platform.

10. Limitation of Liability

Wayja is not liable for any losses or damages arising from the use of the Platform. We do not guarantee the accuracy of market information or the availability of the Platform at all times.

11. Changes to Terms

We reserve the right to modify these terms at any time. Users will be notified of significant changes. Continued use of the Platform after changes constitutes acceptance of the new terms.

12. Contact Information

For questions about these terms, please contact our support team through the Platform or email us at support@wayja.me.`,
      version: 1
    };

    try {
      const { error } = await supabase
        .rpc('add_site_content', {
          p_content_type: 'terms',
          p_title: initialTerms.title,
          p_content: initialTerms.content,
          p_version: initialTerms.version,
          p_is_active: true,
          p_order: 1
        });

      if (error) throw error;
    } catch (err) {
      logger.error('Error creating initial terms:', err);
      throw err;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-600 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-yellow-600 text-sm">
          Terms and conditions are not available at this time.
        </div>
      </div>
    );
  }

  // Parse content into sections
  const sections = content.content.split('\n\n');

  return (
    <div className="bg-white">
      <SEO
        title="Terms and Conditions"
        description="Read Wayja's terms and conditions. Understand the rules, fees, and policies governing our prediction markets platform."
        url="/terms"
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6 border-b pb-4">
          <h1 className="text-xl font-medium text-gray-900">{content.title}</h1>
          <p className="text-xs text-gray-500 mt-1">
            Version {content.version} • Last updated: {new Date(content.updated_at).toLocaleDateString()}
          </p>
        </div>

        <div className="text-sm text-gray-600 leading-relaxed">
          {sections.map((section, index) => {
            const lines = section.split('\n');
            const title = lines[0];
            const contentLines = lines.slice(1);
            
            return (
              <div key={index} className="mb-6" id={`section-${index + 1}`}>
                <h2 className="text-sm font-medium text-gray-900 mb-2">{title}</h2>
                <div>
                  {contentLines.map((line, lineIdx) => {
                    if (line.startsWith('•')) {
                      return (
                        <div key={lineIdx} className="flex">
                          <span className="mr-2">•</span>
                          <span>{line.substring(1).trim()}</span>
                        </div>
                      );
                    }
                    return <p key={lineIdx} className="mb-2">{line}</p>;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            By using Wayja, you agree to these terms and conditions. If you have any questions, please{' '}
            <a href="/contact-support" className="text-wayja-teal hover:underline">contact our support team</a>.
          </p>
        </div>
      </div>
    </div>
  );
} 